import React from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessIcon from "@mui/icons-material/Business";
import InsightsIcon from "@mui/icons-material/Insights";
import HubIcon from "@mui/icons-material/Hub";
import { Link as RouterLink, Navigate, useParams } from "react-router-dom";
import PageTitle from "../components/common/PageTitle";
import AgentRunnersPanel from "../components/platform/AgentRunnersPanel";
import TenantInsightsPanel from "../components/platform/TenantInsightsPanel";
import TenantManagementPanel from "../components/platform/TenantManagementPanel";
import {
  isPlatformDashboardSection,
  PLATFORM_DASHBOARD_PATH,
  platformDashboardSectionPath,
  type PlatformDashboardSection,
} from "../utils/platformDashboardRoutes";

const dashboardCards: {
  id: PlatformDashboardSection;
  title: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    id: "tenant-management",
    title: "Tenant Management",
    description:
      "Create tenants, enable or disable access, and manage organization settings and tenant admins.",
    icon: <BusinessIcon sx={{ fontSize: 40, color: "primary.main" }} />,
  },
  {
    id: "tenant-insights",
    title: "Tenant Insights",
    description:
      "View platform-wide tenant counts, user distribution, role breakdowns, and content metrics.",
    icon: <InsightsIcon sx={{ fontSize: 40, color: "primary.main" }} />,
  },
  {
    id: "agent-runners",
    title: "Agent runners",
    description:
      "Bind each workspace agent to an agent-runner base path for all tenants or one tenant.",
    icon: <HubIcon sx={{ fontSize: 40, color: "primary.main" }} />,
  },
];

const PlatformDashboardPage: React.FC = () => {
  const { section } = useParams<{ section?: string }>();
  const activeSection = isPlatformDashboardSection(section) ? section : null;

  if (section && !activeSection) {
    return <Navigate to={PLATFORM_DASHBOARD_PATH} replace />;
  }

  const sectionTitle =
    activeSection === "tenant-management"
      ? "Tenant Management"
      : activeSection === "tenant-insights"
        ? "Tenant Insights"
        : activeSection === "agent-runners"
          ? "Agent runners"
          : "Platform Dashboard";

  const sectionDescription =
    activeSection === "tenant-management"
      ? "Manage tenants, organization settings, and tenant admins across the platform."
      : activeSection === "tenant-insights"
        ? "Platform-wide metrics for tenants, users, roles, and content."
        : activeSection === "agent-runners"
          ? "Choose which agent-runner base path each workspace agent uses, for all tenants or one tenant."
          : "Platform administration tools for SAMI X admins.";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <PageTitle pageTitle={sectionTitle} pageDescription={sectionDescription} />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {activeSection && (
          <Button
            component={RouterLink}
            to={PLATFORM_DASHBOARD_PATH}
            startIcon={<ArrowBackIcon />}
            sx={{ mb: 3 }}
          >
            Back to dashboard
          </Button>
        )}

        {!activeSection && (
          <Grid container spacing={3}>
            {dashboardCards.map((card) => (
              <Grid item xs={12} md={6} key={card.id}>
                <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                  <CardActionArea
                    component={RouterLink}
                    to={platformDashboardSectionPath(card.id)}
                    sx={{ height: "100%", alignItems: "stretch" }}
                  >
                    <CardContent sx={{ p: 3, height: "100%" }}>
                      <Box sx={{ mb: 2 }}>{card.icon}</Box>
                      <Typography variant="h6" fontWeight={700} gutterBottom>
                        {card.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {card.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {activeSection === "tenant-management" && <TenantManagementPanel />}
        {activeSection === "tenant-insights" && <TenantInsightsPanel />}
        {activeSection === "agent-runners" && <AgentRunnersPanel />}
      </Container>
    </Box>
  );
};

export default PlatformDashboardPage;
